/**
 * Roof edge calculations and classification utilities
 */

export interface Point {
  lat: number;
  lng: number;
}

export interface Edge {
  id: string;
  start: Point;
  end: Point;
  lengthFt: number;
  type: 'ridge' | 'hip' | 'valley' | 'eave' | 'rake';
  facetIds: number[];
}

export interface FacetData {
  id: number;
  areaSqFt: number;
  tiltDegrees: number;
  azimuthDegrees: number;
  pitchRatio: string;
  orientation: string;
  boundingBox?: {
    sw: Point;
    ne: Point;
  };
}

/**
 * Convert tilt degrees to roofer pitch ratio (X/12)
 */
export function tiltToPitchRatio(tiltDeg: number | null | undefined): { pitch: string; angle: number } | null {
  // Defensive check: ensure valid number
  if (typeof tiltDeg !== 'number' || isNaN(tiltDeg)) {
    return null;
  }
  
  // Convert degrees → pitch ratio
  const rise = Math.tan((tiltDeg * Math.PI) / 180) * 12;

  // Convert rise to nearest common roofing pitch
  const nearest = Math.round(rise);

  const cleanRise = nearest < 1 ? 1 : nearest; // no 0/12 pitches

  return {
    pitch: `${cleanRise}/12`,
    angle: Math.round(tiltDeg),
  };
}

/**
 * Get cardinal direction from azimuth degrees
 */
export function azimuthToOrientation(azimuthDeg: number): string {
  if (azimuthDeg < 45 || azimuthDeg >= 315) return 'North';
  if (azimuthDeg >= 45 && azimuthDeg < 135) return 'East';
  if (azimuthDeg >= 135 && azimuthDeg < 225) return 'South';
  return 'West';
}

/**
 * Calculate distance between two lat/lng points using Haversine formula
 */
export function haversineDistance(p1: Point, p2: Point): number {
  const R = 6371e3; // Earth radius in meters
  const lat1 = (p1.lat * Math.PI) / 180;
  const lat2 = (p2.lat * Math.PI) / 180;
  const dLat = ((p2.lat - p1.lat) * Math.PI) / 180;
  const dLng = ((p2.lng - p1.lng) * Math.PI) / 180;

  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const meters = R * c;

  // Convert meters to feet
  return meters * 3.28084;
}

/**
 * Check if two points are approximately equal (within tolerance)
 */
function pointsEqual(p1: Point, p2: Point, tolerance = 0.000001): boolean {
  return Math.abs(p1.lat - p2.lat) < tolerance && Math.abs(p1.lng - p2.lng) < tolerance;
}

/**
 * Classify edge type based on adjacent facets
 */
function classifyEdge(
  facet1: FacetData,
  facet2: FacetData | null
): 'ridge' | 'hip' | 'valley' | 'eave' | 'rake' {
  // Perimeter edges (no adjacent facet)
  if (!facet2) {
    // Use tilt to determine if it's eave or rake
    if (facet1.tiltDegrees < 15) return 'eave';
    return 'rake';
  }

  const tiltDiff = Math.abs(facet1.tiltDegrees - facet2.tiltDegrees);
  const azimuthDiff = Math.abs(facet1.azimuthDegrees - facet2.azimuthDegrees);

  // Ridge: Two facets slope away from each other
  if (tiltDiff < 5 && azimuthDiff < 25) {
    return 'ridge';
  }

  // Valley: Two facets slope towards each other, narrow interior angle
  if (azimuthDiff > 150) {
    return 'valley';
  }

  // Hip: Two facets slope towards each other, different azimuths
  if (azimuthDiff >= 30 && azimuthDiff <= 150) {
    return 'hip';
  }

  // Rake: High tilt difference
  if (tiltDiff > 15) {
    return 'rake';
  }

  // Default to eave for lower edges
  return 'eave';
}

/**
 * Parse Solar API response and extract roof edges with classification
 */
export function parseRoofEdges(roofData: any): {
  edges: Edge[];
  facets: FacetData[];
  totals: {
    ridgeLengthFt: number;
    hipLengthFt: number;
    valleyLengthFt: number;
    eaveLengthFt: number;
    rakeLengthFt: number;
    perimeterFt: number;
  };
} {
  const facets: FacetData[] = [];
  const edges: Edge[] = [];

  // Parse facets
  roofData.planes.forEach((plane: any, idx: number) => {
    const pitchInfo = tiltToPitchRatio(plane.tiltDegrees || 0);
    facets.push({
      id: idx,
      areaSqFt: plane.areaSqFt,
      tiltDegrees: plane.tiltDegrees || 0,
      azimuthDegrees: plane.azimuthDegrees || 0,
      pitchRatio: pitchInfo.pitch,
      orientation: azimuthToOrientation(plane.azimuthDegrees || 0),
    });
  });

  // Create simplified edge extraction from bounding boxes
  // In a real implementation, you'd parse actual polygons from the Solar API
  facets.forEach((facet, idx) => {
    // Create 4 edges from each facet (simplified perimeter)
    const baseLength = Math.sqrt(facet.areaSqFt) / Math.cos((facet.tiltDegrees * Math.PI) / 180);
    
    // Determine edge type based on position and adjacent facets
    const edgeTypes: Array<'ridge' | 'hip' | 'valley' | 'eave' | 'rake'> = ['ridge', 'hip', 'valley', 'eave'];
    
    for (let i = 0; i < 4; i++) {
      const adjacentFacet = idx > 0 && i < 2 ? facets[idx - 1] : null;
      const edgeType = classifyEdge(facet, adjacentFacet);
      
      edges.push({
        id: `edge-${idx}-${i}`,
        start: { lat: 0, lng: 0 }, // Simplified - in real implementation parse from polygons
        end: { lat: 0, lng: 0 },
        lengthFt: baseLength / 4,
        type: edgeType,
        facetIds: adjacentFacet ? [idx, adjacentFacet.id] : [idx],
      });
    }
  });

  // Calculate totals by edge type
  const totals = {
    ridgeLengthFt: 0,
    hipLengthFt: 0,
    valleyLengthFt: 0,
    eaveLengthFt: 0,
    rakeLengthFt: 0,
    perimeterFt: 0,
  };

  edges.forEach((edge) => {
    totals.perimeterFt += edge.lengthFt;
    
    switch (edge.type) {
      case 'ridge':
        totals.ridgeLengthFt += edge.lengthFt;
        break;
      case 'hip':
        totals.hipLengthFt += edge.lengthFt;
        break;
      case 'valley':
        totals.valleyLengthFt += edge.lengthFt;
        break;
      case 'eave':
        totals.eaveLengthFt += edge.lengthFt;
        break;
      case 'rake':
        totals.rakeLengthFt += edge.lengthFt;
        break;
    }
  });

  return { edges, facets, totals };
}
